import { HttpErrorResponse } from '@angular/common/http';

import {
  createEntityCollectionStore,
  CreateOneOptimisticPayload,
  getEntityCollectionActions,
} from '@recruitee/ngrx';
import type { PersonalToken, PersonalTokenUpdate } from '@recruitee/personal-tokens-types';

const idSelector = ({ id }: { id: string }) => id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<PersonalToken>()(
  'PersonalToken',
  {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        fetchOne: (state, payload: { id: string; secureChangeToken: string }) =>
          defaultActions.fetchOne(state, payload),

        createOnePessimistic: (
          state,
          payload: CreateOneOptimisticPayload<{ name: string; secureChangeToken: string }>,
        ) => defaultActions.createOnePessimistic(state, payload),

        removeOnePessimistic: (state, payload: { id: string; secureChangeToken: string }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        removeOnePessimisticSuccess: (state, payload: { id: string }) =>
          adapter.removeOne(state, payload.id),

        updateName: (state, payload: { data: PersonalTokenUpdate }) =>
          defaultActions.updateOnePessimistic(state, payload),
        updateNameSuccess: defaultActions.updateOnePessimisticSuccess,
        updateNameFailed: (
          state,
          payload: { data: PersonalTokenUpdate; error: HttpErrorResponse },
        ) =>
          adapter.setError(state, payload.error, {
            entities: [idSelector(payload.data)],
            scope: null,
          }),
      };
    },
  },
);

export type Actions = typeof actions;
const PersonalTokenActions: Pick<
  Actions,
  | 'fetchCollection'
  | 'fetchCollectionSuccess'
  | 'fetchCollectionFailed'
  | 'fetchOne'
  | 'fetchOneSuccess'
  | 'fetchOneFailed'
  | 'createOnePessimistic'
  | 'createOnePessimisticSuccess'
  | 'createOnePessimisticFailed'
  | 'removeOnePessimistic'
  | 'removeOnePessimisticSuccess'
  | 'removeOnePessimisticFailed'
  | 'updateName'
  | 'updateNameSuccess'
  | 'updateNameFailed'
> = actions;

export {
  PersonalTokenActions,
  actions,
  selectors as PersonalTokenSelectors,
  reducer as PersonalTokenReducer,
  namespace as PersonalTokenNamespace,
};
